import { BufReader, BufWriter, ImportArgsSerializer, LoadArgsSerializer, LoadByOriginArgsSerializer, NewArgsSerializer, CallArgsSerializer, ExecArgsSerializer, FundArgsSerializer, LockArgsSerializer, DeployArgsSerializer, SignArgsSerializer, SignToArgsSerializer, UnknownInstruction } from './internal.js';
/**
 * All OpCode bytes.
 */
export var OpCode;
(function (OpCode) {
    // Loading
    OpCode[OpCode["IMPORT"] = 161] = "IMPORT";
    OpCode[OpCode["LOAD"] = 162] = "LOAD";
    OpCode[OpCode["LOADBYORIGIN"] = 163] = "LOADBYORIGIN";
    // Calling
    OpCode[OpCode["NEW"] = 177] = "NEW";
    OpCode[OpCode["CALL"] = 178] = "CALL";
    OpCode[OpCode["EXEC"] = 179] = "EXEC";
    // Output
    OpCode[OpCode["FUND"] = 193] = "FUND";
    OpCode[OpCode["LOCK"] = 194] = "LOCK";
    // Code
    OpCode[OpCode["DEPLOY"] = 209] = "DEPLOY";
    // Cryptography
    OpCode[OpCode["SIGN"] = 225] = "SIGN";
    OpCode[OpCode["SIGNTO"] = 226] = "SIGNTO";
})(OpCode || (OpCode = {}));
/**
 * Instruction base class.
 *
 * An Instruction is Aldea's smallest contiguous unit of execution. A
 * transaction consists of a `OpCode` byte and a number of arguments, depending
 * on the `OpCode`.
 */
export class Instruction {
    opcode;
    constructor(opcode) {
        this.opcode = opcode;
    }
    static fromBytes(bytes) {
        const buf = new BufReader(bytes);
        return buf.read(InstructionSerializer);
    }
    toBytes() {
        const buf = new BufWriter();
        buf.write(InstructionSerializer, this);
        return buf.data;
    }
}
/**
 * InstructionRef class - just a wrapper around number
 */
export class InstructionRef {
    idx;
    constructor(idx) {
        if (!Number.isInteger(idx))
            throw new Error('invalid ref. must be an integer.');
        this.idx = idx;
    }
    get [Symbol.toStringTag]() { return `InstructionRef`; }
    toString() { return `${this[Symbol.toStringTag]}<${this.idx}>`; }
}
/**
 * Wrap a number with InstructionRef
 */
export function ref(idx) {
    return new InstructionRef(idx);
}
/**
 * Instruction Serializer object - implements the Serializable interface.
 */
export const InstructionSerializer = {
    read(buf) {
        const opcode = buf.readU8();
        const argsBuf = buf.readBytes();
        const args = new BufReader(argsBuf);
        switch (opcode) {
            case OpCode.IMPORT:
                return args.read(ImportArgsSerializer);
            case OpCode.LOAD:
                return args.read(LoadArgsSerializer);
            case OpCode.LOADBYORIGIN:
                return args.read(LoadByOriginArgsSerializer);
            case OpCode.NEW:
                return args.read(NewArgsSerializer);
            case OpCode.CALL:
                return args.read(CallArgsSerializer);
            case OpCode.EXEC:
                return args.read(ExecArgsSerializer);
            case OpCode.FUND:
                return args.read(FundArgsSerializer);
            case OpCode.LOCK:
                return args.read(LockArgsSerializer);
            case OpCode.DEPLOY:
                return args.read(DeployArgsSerializer);
            case OpCode.SIGN:
                return args.read(SignArgsSerializer);
            case OpCode.SIGNTO:
                return args.read(SignToArgsSerializer);
            default: return new UnknownInstruction(opcode, argsBuf);
        }
    },
    write(buf, inst) {
        const args = new BufWriter();
        switch (inst.opcode) {
            case OpCode.IMPORT:
                args.write(ImportArgsSerializer, inst);
                break;
            case OpCode.LOAD:
                args.write(LoadArgsSerializer, inst);
                break;
            case OpCode.LOADBYORIGIN:
                args.write(LoadByOriginArgsSerializer, inst);
                break;
            case OpCode.NEW:
                args.write(NewArgsSerializer, inst);
                break;
            case OpCode.CALL:
                args.write(CallArgsSerializer, inst);
                break;
            case OpCode.EXEC:
                args.write(ExecArgsSerializer, inst);
                break;
            case OpCode.FUND:
                args.write(FundArgsSerializer, inst);
                break;
            case OpCode.LOCK:
                args.write(LockArgsSerializer, inst);
                break;
            case OpCode.DEPLOY:
                args.write(DeployArgsSerializer, inst);
                break;
            case OpCode.SIGN:
                args.write(SignArgsSerializer, inst);
                break;
            case OpCode.SIGNTO:
                args.write(SignToArgsSerializer, inst);
                break;
            default:
                args.writeFixedBytes(inst.argsBuf);
        }
        const argsBuf = args.data;
        buf.writeU8(inst.opcode);
        buf.writeBytes(argsBuf);
        return buf;
    }
};
